/**
 * Sơ đồ kho Summary Nhơn Trạch — bám Access `frmFactoryNtSummarySubForm2`.
 *
 *   KHO A (Label496, Top=7068):  4 hàng × 8 cột (rows 4→1, cols A→H)
 *                                32 ô = NT.A.{col}{row}, vd "NT.A.A4".
 *                                Controls: AA4_V..AH4_V (row 4) → AA1_V..AH1_V (row 1).
 *
 *   KHO C (Label937, Top=11484): 8 hàng × 4 cột (rows 8→1, cols A→D)
 *                                28 ô = NT.C.{col}{row}.
 *                                Controls: CA8_V..CD8_V (row 8) → CA1_V..CD1_V.
 *                                Thiếu CA3, CA4, CA5, CA6 — render ô trống.
 *
 * Lookup tồn: match Access ControlSource `full_area_code='NT.<zone>.<col><row>'`
 * trong `tlc_factory.view_map_stock`.
 *
 * Note: SubForm2 không có banner KVSX/Hàng dở dang trong khu vực map — WIP
 * total hiển thị ở quality table section (parent FactorySummary), không
 * trong component này.
 */
import { Fragment, useMemo } from 'react'
import { alpha, type Theme } from '@mui/material/styles'
import { Box, Paper, Stack, Typography } from '@mui/material'
import type { FactoryCell } from '@/store/api/factoryApi'
import type { FactorySummaryMapCell } from '../baoLoc/FactoryBaoLocSummaryMap'

interface Props {
  mapCells: FactorySummaryMapCell[]
  sumRow?: Record<string, FactoryCell> | null
  kvSx?: number | null
  scope?: 'all' | 'kho-a' | 'kho-bc' | 'allocation'
  title?: string
  subtitle?: string
  factoryName?: string
}

function asNumber(v: FactoryCell): number | null {
  if (v === null || v === undefined || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

function fmtQty(v: number | null): string {
  if (v === null || !Number.isFinite(v)) return '—'
  return v.toLocaleString('en-US', { maximumFractionDigits: 0 })
}

// Access spec: KHO A = 4 rows × 8 cols
const KHO_A_COLS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'] as const
const KHO_A_ROWS = [4, 3, 2, 1] as const

// Access spec: KHO C = 8 rows × 4 cols
const KHO_C_COLS = ['A', 'B', 'C', 'D'] as const
const KHO_C_ROWS = [8, 7, 6, 5, 4, 3, 2, 1] as const
// Cells missing trong Access spec (col A rows 3-6 — không có textbox CA3..CA6_V)
const KHO_C_MISSING = new Set(['A3', 'A4', 'A5', 'A6'])

const cellSx = {
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  justifyContent: 'center',
  px: 0.25,
  py: 0.25,
  minHeight: 40,
  textAlign: 'center' as const,
}

const headerCellSx = {
  ...cellSx,
  bgcolor: 'action.hover',
  minHeight: 20,
  fontSize: '0.62rem',
  fontWeight: 800,
}

const sectionTitleSx = {
  display: 'block',
  mb: 0.5,
  fontWeight: 800,
  fontSize: '0.72rem',
  letterSpacing: 0.04,
  color: 'primary.dark',
}

type Zone = 'A' | 'C'

export function NhonTrachSummaryMap({
  mapCells,
  kvSx,
  title,
  subtitle,
  scope = 'all',
}: Props) {
  // Lookup tồn theo full_area_code `NT.<zone>.<col><row>` (vd "NT.A.A4").
  const lookup = useMemo(() => {
    const byFull = new Map<string, number>()
    for (const c of mapCells) {
      const v = asNumber(c.value) ?? 0
      if (c.field) {
        const k = String(c.field).trim().toUpperCase()
        if (k) byFull.set(k, (byFull.get(k) ?? 0) + v)
      }
    }
    return (zone: Zone, col: string, row: number): number | null => {
      const k = `NT.${zone}.${col.toUpperCase()}${row}`
      return byFull.get(k) ?? null
    }
  }, [mapCells])

  // Zone totals: sum tất cả NT.<zone>.* (bao gồm cả cells không nằm trong
  // Access form spec — defensive nếu DB có thêm cells).
  const zoneTotals = useMemo(() => {
    const t: Record<Zone, number> = { A: 0, C: 0 }
    for (const c of mapCells) {
      const f = (c.field || '').toUpperCase()
      const n = asNumber(c.value) ?? 0
      if (f.startsWith('NT.A.')) t.A += n
      else if (f.startsWith('NT.C.')) t.C += n
    }
    return t
  }, [mapCells])

  const kv = asNumber(kvSx ?? null) ?? 0
  const grandTotal = zoneTotals.A + zoneTotals.C + kv

  const showKhoA = scope === 'all' || scope === 'kho-a'
  // 'kho-bc' giữ tên scope cũ để tương thích parent — NT chỉ có kho C.
  const showKhoC = scope === 'all' || scope === 'kho-bc'
  const showAllocation = scope === 'all' || scope === 'allocation'

  return (
    <Paper variant="outlined" sx={{ p: 1.25, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="subtitle2" fontWeight={800} sx={{ letterSpacing: 0.02, mb: 1 }}>
        {title ?? 'WAREHOUSE MAP'}
      </Typography>

      {/* 2 cột: bên trái map (KHO A + KHO C), bên phải allocation. Tỷ lệ 8:2. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '8fr 2fr' },
          gap: 1.5,
          alignItems: 'start',
        }}
      >
      <Box sx={{ minWidth: 0 }}>

      {/* KHO A — 4 hàng × 8 cột (Access Label496 + AA4_V..AH1_V) */}
      {showKhoA && (
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography component="span" sx={sectionTitleSx}>KHO A</Typography>
            <Typography variant="caption" color="text.secondary">Σ {fmtQty(zoneTotals.A)}</Typography>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `28px repeat(${KHO_A_COLS.length}, minmax(0, 1fr))`,
              gap: 0,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 0.5,
              overflow: 'hidden',
            }}
          >
            {/* Col header row */}
            <Box sx={{ ...headerCellSx, borderTop: 0, borderLeft: 0 }} />
            {KHO_A_COLS.map((col) => (
              <Box key={`hdr-A-${col}`} sx={{ ...headerCellSx, borderTop: 0 }}>
                {col}
              </Box>
            ))}
            {/* Data rows */}
            {KHO_A_ROWS.map((row) => (
              <Fragment key={`row-A-${row}`}>
                <Box sx={{ ...headerCellSx, borderLeft: 0 }}>{row}</Box>
                {KHO_A_COLS.map((col) => {
                  const qty = lookup('A', col, row)
                  const hasData = qty !== null && qty !== 0
                  return (
                    <Box
                      key={`cell-A-${col}${row}`}
                      sx={{
                        ...cellSx,
                        bgcolor: (t: Theme) => alpha(t.palette.primary.main, 0.04),
                      }}
                      title={`NT.A.${col}${row}`}
                    >
                      <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, lineHeight: 1.05 }}>
                        {col}{row}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.7rem',
                          fontWeight: hasData ? 800 : 400,
                          color: hasData ? 'text.primary' : 'text.disabled',
                          lineHeight: 1.05,
                        }}
                      >
                        {fmtQty(qty)}
                      </Typography>
                    </Box>
                  )
                })}
              </Fragment>
            ))}
          </Box>
        </Box>
      )}

      {/* KHO C — 8 hàng × 4 cột (Access Label937 + CA8_V..CD1_V, thiếu CA3..CA6) */}
      {showKhoC && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography component="span" sx={sectionTitleSx}>KHO C</Typography>
            <Typography variant="caption" color="text.secondary">Σ {fmtQty(zoneTotals.C)}</Typography>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `28px repeat(${KHO_C_COLS.length}, minmax(0, 1fr))`,
              gap: 0,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 0.5,
              overflow: 'hidden',
              maxWidth: 360,
            }}
          >
            <Box sx={{ ...headerCellSx, borderTop: 0, borderLeft: 0 }} />
            {KHO_C_COLS.map((col) => (
              <Box key={`hdr-C-${col}`} sx={{ ...headerCellSx, borderTop: 0 }}>
                {col}
              </Box>
            ))}
            {KHO_C_ROWS.map((row) => (
              <Fragment key={`row-C-${row}`}>
                <Box sx={{ ...headerCellSx, borderLeft: 0 }}>{row}</Box>
                {KHO_C_COLS.map((col) => {
                  const cellId = `${col}${row}`
                  const missing = KHO_C_MISSING.has(cellId)
                  const qty = missing ? null : lookup('C', col, row)
                  const hasData = qty !== null && qty !== 0
                  return (
                    <Box
                      key={`cell-C-${cellId}`}
                      sx={{
                        ...cellSx,
                        bgcolor: missing
                          ? 'transparent'
                          : (t: Theme) => alpha(t.palette.success.main, 0.04),
                      }}
                      title={missing ? '' : `NT.C.${cellId}`}
                    >
                      {missing ? null : (
                        <>
                          <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, lineHeight: 1.05 }}>
                            {cellId}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.7rem',
                              fontWeight: hasData ? 800 : 400,
                              color: hasData ? 'text.primary' : 'text.disabled',
                              lineHeight: 1.05,
                            }}
                          >
                            {fmtQty(qty)}
                          </Typography>
                        </>
                      )}
                    </Box>
                  )
                })}
              </Fragment>
            ))}
          </Box>
        </Box>
      )}

      </Box>

      {/* Cột phải: STOCK ALLOCATION (tỷ lệ 2/10) */}
      {showAllocation && (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ letterSpacing: 0.02, mb: 0.75, fontSize: '0.78rem' }}>
            STOCK ALLOCATION
          </Typography>
          <Stack spacing={0.5}>
            <AllocLine label="Total (stack + WIP)" value={grandTotal} bold />
            <AllocLine label="Kho A (NT.A.*)" value={zoneTotals.A} />
            <AllocLine label="Kho C (NT.C.*)" value={zoneTotals.C} />
            <AllocLine label="WIP area" value={kv} />
          </Stack>
        </Box>
      )}
      </Box>
    </Paper>
  )
}

function AllocLine({ label, value, bold }: { label: string; value: number; bold?: boolean }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '0.78rem',
        fontWeight: bold ? 700 : 400,
      }}
    >
      <span>{label}</span>
      <span style={{ fontWeight: 600 }}>{fmtQty(value)}</span>
    </Box>
  )
}

export default NhonTrachSummaryMap
